// The web's only contact with the Supabase client library. It is loaded by dynamic
// import so the admin-mode bundle never contains the library.
/** What getAuthorizationDetails found: consent is needed, or the person already consented. */
export type AuthorizationDetails =
  | { kind: 'consent'; authorizationId: string; clientName: string; redirectUri: string; scope: string }
  | { kind: 'redirect'; redirectUrl: string };

export interface SupabaseAuth {
  signInWithEmail(email: string, captchaToken: string | null): Promise<void>;
  signInWithPassword(email: string, password: string, captchaToken: string | null): Promise<void>;
  signInWithGoogle(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  signOut(): Promise<void>;
  getAuthorizationDetails(authorizationId: string): Promise<AuthorizationDetails>;
  /** Resolves the URL to send the browser to; never redirects by itself. */
  approveAuthorization(authorizationId: string): Promise<string>;
  denyAuthorization(authorizationId: string): Promise<string>;
  /** Revokes this client's grant, with the person's session. */
  revokeGrant(clientId: string): Promise<void>;
}

export async function loadSupabaseAuth(
  config: { supabaseUrl: string; supabaseAnonKey: string },
  origin: string,
): Promise<SupabaseAuth> {
  const { createClient } = await import('@supabase/supabase-js');
  const client = createClient(config.supabaseUrl, config.supabaseAnonKey);
  const redirectTo = `${origin}/auth/callback`;
  return {
    async signInWithEmail(email, captchaToken) {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, ...(captchaToken ? { captchaToken } : {}) },
      });
      if (error) throw error;
    },
    async signInWithPassword(email, password, captchaToken) {
      const { error } = await client.auth.signInWithPassword({
        email,
        password,
        ...(captchaToken ? { options: { captchaToken } } : {}),
      });
      if (error) throw error;
    },
    async signInWithGoogle() {
      const { error } = await client.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } });
      if (error) throw error;
    },
    async getAccessToken() {
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      return data.session?.access_token ?? null;
    },
    async signOut() {
      // Forget the session in this browser first — no network, so sign-out never
      // waits on Supabase — then revoke it everywhere in the background. The
      // default global signOut() revokes before it forgets, which is what made
      // sign-out stall whenever Supabase answered slowly.
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token ?? null;
      const { error } = await client.auth.signOut({ scope: 'local' });
      if (error) throw error;
      if (token) {
        void fetch(`${config.supabaseUrl}/auth/v1/logout?scope=global`, {
          method: 'POST',
          headers: { apikey: config.supabaseAnonKey, Authorization: `Bearer ${token}` },
          keepalive: true
        }).catch(() => {});
      }
    },
    async getAuthorizationDetails(id) {
      const { data, error } = await client.auth.oauth.getAuthorizationDetails(id);
      if (error) throw error;
      return 'authorization_id' in data
        ? {
            kind: 'consent',
            authorizationId: data.authorization_id,
            clientName: data.client.name,
            redirectUri: data.redirect_uri,
            scope: data.scope,
          }
        : { kind: 'redirect', redirectUrl: data.redirect_url };
    },
    async approveAuthorization(id) {
      const { data, error } = await client.auth.oauth.approveAuthorization(id, { skipBrowserRedirect: true });
      if (error) throw error;
      return data.redirect_url;
    },
    async denyAuthorization(id) {
      const { data, error } = await client.auth.oauth.denyAuthorization(id, { skipBrowserRedirect: true });
      if (error) throw error;
      return data.redirect_url;
    },
    async revokeGrant(clientId) {
      const { error } = await client.auth.oauth.revokeGrant({ clientId });
      // A grant already deleted answers 404 `oauth_consent_not_found` (observed on GoTrue v2.197.0
      // with supabase-js 2.117.2; re-check after upgrading either):
      // that is the outcome revoke wants, so a retry is not an error.
      if (error && error.code !== 'oauth_consent_not_found') throw error;
    },
  };
}
